import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  // Landing page — 首页 (public)
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { title: 'AITODO - AI智能任务管理', requiresAuth: false }
  },
  // Auth routes (AuthLayout)
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/LoginView.vue'),
        meta: { title: '登录', requiresAuth: false }
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/views/RegisterView.vue'),
        meta: { title: '注册', requiresAuth: false }
      }
    ]
  },
  // Main app routes (MainLayout)
  {
    path: '/app',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: '仪表盘', requiresAuth: true, keepAlive: true }
      },
      {
        path: 'tasks',
        name: 'Tasks',
        component: () => import('@/views/TasksView.vue'),
        meta: { title: '任务管理', requiresAuth: true, keepAlive: true }
      },
      {
        path: 'tasks/:id',
        name: 'TaskDetail',
        component: () => import('@/views/TaskDetailView.vue'),
        meta: { title: '任务详情', requiresAuth: true }
      },
      {
        path: 'daily-plan',
        name: 'DailyPlan',
        component: () => import('@/views/DailyPlanView.vue'),
        meta: { title: '每日计划', requiresAuth: true, keepAlive: true }
      },
      {
        path: 'calendar',
        name: 'Calendar',
        component: () => import('@/views/CalendarView.vue'),
        meta: { title: '日程日历', requiresAuth: true, keepAlive: true }
      },
      {
        path: 'daily-checkin',
        name: 'DailyCheckin',
        component: () => import('@/views/DailyCheckinView.vue'),
        meta: { title: '每日签入', requiresAuth: true }
      },
      {
        path: 'statistics',
        name: 'Statistics',
        component: () => import('@/views/StatisticsView.vue'),
        meta: { title: '统计分析', requiresAuth: true }
      },
      {
        path: 'achievements',
        name: 'Achievements',
        component: () => import('@/views/AchievementsView.vue'),
        meta: { title: '成就中心', requiresAuth: true }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { title: '个人设置', requiresAuth: true }
      },
      {
        path: 'decomposition-wizard',
        name: 'DecompositionWizard',
        component: () => import('@/views/DecompositionWizardView.vue'),
        meta: { title: 'AI任务分解', requiresAuth: true }
      },
      {
        path: 'assistant',
        name: 'Assistant',
        component: () => import('@/views/AssistantView.vue'),
        meta: { title: 'AI助手', requiresAuth: true }
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: () => import('@/views/NotificationsView.vue'),
        meta: { title: '通知中心', requiresAuth: true }
      },
      {
        path: 'templates',
        name: 'Templates',
        component: () => import('@/views/TemplatesView.vue'),
        meta: { title: '任务模板', requiresAuth: true }
      },
      {
        path: 'goals',
        name: 'Goals',
        component: () => import('@/views/GoalsView.vue'),
        meta: { title: '长期目标', requiresAuth: true, keepAlive: true }
      }
    ]
  },
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: '页面不存在' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 }
  }
})

// Simple JWT expiry check (without library dependency)
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 < Date.now() : false
  } catch {
    return true
  }
}

// Route guard
router.beforeEach((to, _from, next) => {
  // Update page title
  const title = to.meta.title ? `${to.meta.title} - AITODO` : 'AITODO'
  document.title = title

  const token = localStorage.getItem('token')
  const validToken = token && !isTokenExpired(token)

  // Clear stale token automatically
  if (token && !validToken) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  if (to.meta.requiresAuth && !validToken) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if ((to.name === 'Login' || to.name === 'Register') && validToken) {
    next({ name: 'Dashboard' })
  } else {
    next()
  }
})

export default router
